/**
 * pages/marketing/MarketingDelivery.jsx · v2.7.57+E34 · Section 1
 * ═══════════════════════════════════════════════════════════════════════
 * DELIVERY SYSTEM · Section 1 · Multi-Stop Delivery Intake Form.
 *
 * Public-facing route at `/delivery` accessible from the primary marketing
 * navigation bar. Collects everything Sections 2-5 need downstream:
 *   • Customer contact info + final drop-off address + requested pickup time
 *   • 1 to 5 repeatable "Delivery Stop" blocks (business name, address,
 *     order#, item count, receipt image/PDF upload)
 *   • Gratuity selector (10% / 15% / 20% / Custom $)
 *
 * On submit:
 *   1. POST /api/deliveries → server issues an `owner_token`
 *   2. For each stop with an attached receipt file → POST
 *      /api/deliveries/{id}/receipt?stop_index=N&token=...  (multipart)
 *   3. Show a confirmation panel with the request ID
 *      (Section 2 will replace the "will be quoted" message with a live
 *      instant quote before payment.)
 *
 * ⚠️ STAGE GATE — This file MUST NOT contain pricing/quote logic, admin
 * dispatch board hooks, or driver-side flows. Those land in Sections 2/3/4.
 */
import { useEffect, useMemo, useState } from "react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import SmsConsent from "@/components/marketing/SmsConsent";
import {
  Truck, MapPin, User, Mail, Phone, Clock, Store, Hash, FileImage, Plus, X,
  DollarSign, CheckCircle2, ArrowRight, MessageSquare, Bell, Receipt, Info,
} from "lucide-react";

const MAX_STOPS = 5;
const EMPTY_STOP = () => ({
  id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  business_name: "",
  business_address: "",
  order_number: "",
  item_count: "1",
  receipt_file: null,
});

const TIP_PRESETS = [10, 15, 20];

export default function MarketingDelivery() {
  const [customer, setCustomer] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    dropoff_address: "",
    pickup_requested_at: "",
  });
  // v2.7.58+E38 · per-channel notification opt-in. Both default TRUE — a
  // customer can uncheck either box to silence one channel while keeping
  // the other; the server honours these flags in
  // services/customer_notifications.py.
  const [notify, setNotify] = useState({ sms: true, email: true });
  const [stops, setStops] = useState([EMPTY_STOP()]);
  const [tip, setTip] = useState({ kind: "percent", percent: 15, custom: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {id, owner_token, ...} on success
  // v2.7.63+E46 · Pricing Transparency: live itemized preview quote
  // populated by /api/deliveries/estimate. Debounced 300ms after any
  // change to stop count / tip so the customer sees an updating total
  // without hammering the endpoint on every keystroke.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // v2.7.65+E165 · TCPA-style SMS consent — recorded server-side with the
  // delivery request. Optional (form still submits when unchecked); the
  // disclosure block is always visible above the submit button.
  const [smsConsent, setSmsConsent] = useState(false);

  const canAddStop = stops.length < MAX_STOPS;

  const addStop = () => {
    if (!canAddStop) return;
    setStops((s) => [...s, EMPTY_STOP()]);
  };
  const removeStop = (idx) => {
    setStops((s) => (s.length === 1 ? s : s.filter((_, i) => i !== idx)));
  };
  const updateStop = (idx, field) => (e) => {
    const v = field === "receipt_file" ? (e.target.files?.[0] || null) : e.target.value;
    setStops((s) => s.map((st, i) => (i === idx ? { ...st, [field]: v } : st)));
  };
  const updateCustomer = (field) => (e) => {
    setCustomer((c) => ({ ...c, [field]: e.target.value }));
  };

  // Basic client-side gate on the "Submit" button — server enforces the
  // real validators (min length, non-negative, valid email, ≤5 stops).
  const canSubmit = useMemo(() => {
    if (!customer.customer_name.trim() || !customer.customer_phone.trim()) return false;
    if (!customer.customer_email.trim() || !customer.customer_email.includes("@")) return false;
    if (!customer.dropoff_address.trim()) return false;
    if (stops.length < 1) return false;
    for (const s of stops) {
      if (!s.business_name.trim() || !s.business_address.trim()) return false;
      const n = parseInt(s.item_count, 10);
      if (!Number.isFinite(n) || n < 1) return false;
    }
    if (tip.kind === "custom") {
      const n = parseFloat(tip.custom);
      if (!Number.isFinite(n) || n < 0) return false;
    }
    return true;
  }, [customer, stops, tip]);

  // v2.7.63+E46 · fetch live itemized preview quote whenever stop count
  // or tip inputs change. Debounced 300 ms so a customer typing a custom
  // tip amount doesn't stampede the endpoint.
  useEffect(() => {
    const stopCount = stops.length;
    if (stopCount < 1) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const body = {
          stop_count:        stopCount,
          tip_kind:          tip.kind,
          tip_percent:       tip.kind === "percent" ? tip.percent : null,
          tip_custom_amount: tip.kind === "custom" ? parseFloat(tip.custom) || 0 : null,
        };
        const r = await api.post("/deliveries/estimate", body);
        if (!cancelled) setPreview(r.data);
      } catch (e) {
        console.warn("[delivery] preview quote failed:", e?.response?.data?.detail || e?.message);
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [stops.length, tip.kind, tip.percent, tip.custom]);

  const handleSubmit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      const body = {
        customer_name:       customer.customer_name.trim(),
        customer_phone:      customer.customer_phone.trim(),
        customer_email:      customer.customer_email.trim().toLowerCase(),
        sms_consent:         !!smsConsent,
        sms_consent_ts:      smsConsent ? new Date().toISOString() : null,
        dropoff_address:     customer.dropoff_address.trim(),
        pickup_requested_at: customer.pickup_requested_at
          ? new Date(customer.pickup_requested_at).toISOString()
          : null,
        stops: stops.map((s) => ({
          business_name:    s.business_name.trim(),
          business_address: s.business_address.trim(),
          order_number:     (s.order_number || "").trim(),
          item_count:       parseInt(s.item_count, 10) || 1,
        })),
        tip_kind:          tip.kind,
        tip_percent:       tip.kind === "percent" ? tip.percent : null,
        tip_custom_amount: tip.kind === "custom" ? parseFloat(tip.custom) : null,
        // v2.7.58+E38 · per-channel opt-in for the customer notification pings.
        notify_sms:        !!notify.sms,
        notify_email:      !!notify.email,
      };
      const r = await api.post("/deliveries", body);
      const { id, owner_token } = r.data;
      // Upload any attached receipts. Non-blocking: if a single receipt fails
      // we keep going and surface the count that succeeded / failed at the end.
      let receiptsOk = 0;
      let receiptsFailed = 0;
      for (let i = 0; i < stops.length; i++) {
        const file = stops[i].receipt_file;
        if (!file) continue;
        try {
          const fd = new FormData();
          fd.append("file", file);
          await api.post(
            `/deliveries/${id}/receipt?stop_index=${i}&token=${encodeURIComponent(owner_token)}`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
          receiptsOk += 1;
        } catch (e) {
          receiptsFailed += 1;
          console.warn(`[delivery] receipt upload failed · stop ${i}:`, e?.response?.data?.detail || e?.message);
        }
      }
      const rmsg = receiptsFailed
        ? ` · ${receiptsOk} receipt(s) attached · ${receiptsFailed} failed`
        : receiptsOk
        ? ` · ${receiptsOk} receipt(s) attached`
        : "";
      toast.success(`Delivery request submitted${rmsg}`);
      setResult({ ...r.data, receiptsOk, receiptsFailed });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Submission failed";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please review the form.");
      console.error("[delivery] submit failed:", e?.response?.data || e);
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <MarketingLayout>
        <div className="max-w-2xl mx-auto py-8 px-4" data-testid="delivery-success">
          <Card className="bg-zinc-900 border-emerald-400/40 p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 flex-shrink-0"/>
              <div>
                <h1 className="text-xl font-extrabold text-zinc-100">Delivery Request Received</h1>
                <p className="text-xs text-zinc-400 mt-0.5">Reference ID · <span className="font-mono">{result.id}</span></p>
              </div>
            </div>
            <p className="text-sm text-zinc-300">
              Thanks — our dispatch team will quote your total and confirm the driver
              assignment shortly. You&apos;ll receive an email at{" "}
              <span className="font-semibold text-emerald-300">{customer.customer_email}</span> the moment your quote is ready.
            </p>
            {result.receiptsOk > 0 && (
              <p className="mt-3 text-xs text-emerald-300">
                ✓ {result.receiptsOk} receipt attachment{result.receiptsOk === 1 ? "" : "s"} uploaded.
              </p>
            )}
            {result.receiptsFailed > 0 && (
              <p className="mt-2 text-xs text-amber-300">
                ⚠ {result.receiptsFailed} receipt attachment{result.receiptsFailed === 1 ? "" : "s"} failed to upload — please contact dispatch if this was intentional.
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={() => { setResult(null); setStops([EMPTY_STOP()]); setNotify({ sms: true, email: true }); setCustomer({ customer_name: "", customer_phone: "", customer_email: "", dropoff_address: "", pickup_requested_at: "" }); }}
                variant="outline"
                className="bg-zinc-950 border-zinc-700"
                data-testid="delivery-new-request-btn"
              >
                Submit Another Delivery
              </Button>
              <a href="/" className="inline-flex">
                <Button variant="ghost" data-testid="delivery-home-btn">Back to Home</Button>
              </a>
            </div>
          </Card>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6" data-testid="delivery-header">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-amber-400/15 border border-amber-400/40 flex items-center justify-center flex-shrink-0">
              <Truck className="h-6 w-6 text-amber-300"/>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-100 tracking-tight">Multi-Stop Delivery</h1>
              <p className="text-xs sm:text-sm text-zinc-400">
                Up to {MAX_STOPS} pickups → one drop-off. We&apos;ll quote your total after review.
              </p>
            </div>
          </div>
        </div>

        {/* Customer details */}
        <Card className="bg-zinc-900 border-zinc-800 p-4 sm:p-5 mb-4" data-testid="delivery-customer-card">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Your Details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full Name" icon={<User className="h-3 w-3"/>}>
              <Input value={customer.customer_name} onChange={updateCustomer("customer_name")} placeholder="Jane Doe" data-testid="delivery-customer-name" className="bg-zinc-950 border-zinc-700"/>
            </Field>
            <Field label="Phone" icon={<Phone className="h-3 w-3"/>}>
              <Input value={customer.customer_phone} onChange={updateCustomer("customer_phone")} placeholder="(580) 555-0100" data-testid="delivery-customer-phone" className="bg-zinc-950 border-zinc-700"/>
            </Field>
            <Field label="Email" icon={<Mail className="h-3 w-3"/>}>
              <Input type="email" value={customer.customer_email} onChange={updateCustomer("customer_email")} placeholder="you@example.com" data-testid="delivery-customer-email" className="bg-zinc-950 border-zinc-700"/>
            </Field>
            <Field label="Requested Pickup Time" icon={<Clock className="h-3 w-3"/>}>
              <Input type="datetime-local" value={customer.pickup_requested_at} onChange={updateCustomer("pickup_requested_at")} data-testid="delivery-pickup-time" className="bg-zinc-950 border-zinc-700"/>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Final Delivery Address (Drop-off)" icon={<MapPin className="h-3 w-3"/>}>
                <Input value={customer.dropoff_address} onChange={updateCustomer("dropoff_address")} placeholder="123 Main St, Weatherford OK 73096" data-testid="delivery-dropoff-address" className="bg-zinc-950 border-zinc-700"/>
              </Field>
            </div>
          </div>

          {/* v2.7.58+E38 · Live status notification opt-in. Both channels
              default to ON — a customer can uncheck either to silence that
              channel; the server honours these flags in
              services/customer_notifications.py. */}
          <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="delivery-notify-block">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
              <Bell className="h-3 w-3"/>Live Status Updates
            </div>
            <p className="text-[11px] text-zinc-400 mb-3">
              We&apos;ll ping you when your driver accepts, at each pickup, and when your order is delivered.
              Uncheck either box to silence that channel.
            </p>
            <div className="flex flex-wrap gap-3">
              <label
                className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                  notify.sms
                    ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-200"
                    : "bg-zinc-950 border-zinc-800 text-zinc-500"
                }`}
                data-testid="delivery-notify-sms-label"
              >
                <input
                  type="checkbox"
                  checked={notify.sms}
                  onChange={(e) => setNotify((n) => ({ ...n, sms: e.target.checked }))}
                  className="accent-emerald-400"
                  data-testid="delivery-notify-sms"
                />
                <MessageSquare className="h-3.5 w-3.5"/>
                <span className="text-xs font-medium">Text me updates</span>
              </label>
              <label
                className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                  notify.email
                    ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-200"
                    : "bg-zinc-950 border-zinc-800 text-zinc-500"
                }`}
                data-testid="delivery-notify-email-label"
              >
                <input
                  type="checkbox"
                  checked={notify.email}
                  onChange={(e) => setNotify((n) => ({ ...n, email: e.target.checked }))}
                  className="accent-emerald-400"
                  data-testid="delivery-notify-email"
                />
                <Mail className="h-3.5 w-3.5"/>
                <span className="text-xs font-medium">Email me updates</span>
              </label>
            </div>
          </div>
        </Card>

        {/* Stops (repeatable) */}
        <Card className="bg-zinc-900 border-zinc-800 p-4 sm:p-5 mb-4" data-testid="delivery-stops-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Delivery Stops · {stops.length} of {MAX_STOPS}
            </div>
            {stops.length > 1 && (
              <span className="text-[10px] text-zinc-500">Remove any stop with the ✕ button</span>
            )}
          </div>

          <div className="space-y-4">
            {stops.map((s, idx) => (
              <div
                key={s.id}
                className="p-3 rounded-lg bg-zinc-950 border border-zinc-800"
                data-testid={`delivery-stop-${idx}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-amber-300">Stop {idx + 1}</div>
                  {stops.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStop(idx)}
                      className="text-red-400 hover:text-red-300 transition"
                      data-testid={`delivery-stop-${idx}-remove`}
                      aria-label={`Remove stop ${idx + 1}`}
                    >
                      <X className="h-4 w-4"/>
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Business Name" icon={<Store className="h-3 w-3"/>}>
                    <Input value={s.business_name} onChange={updateStop(idx, "business_name")} placeholder="Whataburger" data-testid={`delivery-stop-${idx}-business`} className="bg-zinc-900 border-zinc-700"/>
                  </Field>
                  <Field label="Pickup Address" icon={<MapPin className="h-3 w-3"/>}>
                    <Input value={s.business_address} onChange={updateStop(idx, "business_address")} placeholder="500 E Main St, Weatherford OK" data-testid={`delivery-stop-${idx}-address`} className="bg-zinc-900 border-zinc-700"/>
                  </Field>
                  <Field label="Order Number" icon={<Hash className="h-3 w-3"/>}>
                    <Input value={s.order_number} onChange={updateStop(idx, "order_number")} placeholder="Optional" data-testid={`delivery-stop-${idx}-order`} className="bg-zinc-900 border-zinc-700"/>
                  </Field>
                  <Field label="Item Count" icon={<Hash className="h-3 w-3"/>}>
                    <Input type="number" min="1" max="999" value={s.item_count} onChange={updateStop(idx, "item_count")} data-testid={`delivery-stop-${idx}-count`} className="bg-zinc-900 border-zinc-700"/>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Upload Receipt Image/PDF (optional)" icon={<FileImage className="h-3 w-3"/>}>
                      <label className="cursor-pointer inline-flex items-center justify-center gap-2 h-10 px-3 border border-zinc-700 rounded bg-zinc-900 text-xs text-zinc-300 hover:bg-zinc-800 w-full sm:w-auto" data-testid={`delivery-stop-${idx}-receipt-label`}>
                        <FileImage className="h-3.5 w-3.5"/>
                        {s.receipt_file ? s.receipt_file.name.slice(0, 40) : "Choose file"}
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={updateStop(idx, "receipt_file")}
                          data-testid={`delivery-stop-${idx}-receipt`}
                        />
                      </label>
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            type="button"
            onClick={addStop}
            disabled={!canAddStop}
            variant="outline"
            className="mt-4 w-full sm:w-auto bg-amber-400/10 border-amber-400/40 text-amber-300 hover:bg-amber-400/20"
            data-testid="delivery-add-stop"
          >
            <Plus className="h-4 w-4 mr-1"/>
            Add Another Stop {canAddStop ? `(${MAX_STOPS - stops.length} remaining)` : "(max reached)"}
          </Button>
        </Card>

        {/* Gratuity selector */}
        <Card className="bg-zinc-900 border-zinc-800 p-4 sm:p-5 mb-4" data-testid="delivery-tip-card">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Driver Gratuity</div>
          <div className="flex flex-wrap gap-2 items-center">
            {TIP_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setTip({ kind: "percent", percent: p, custom: "" })}
                className={`px-3 py-2 rounded font-bold text-sm border transition ${
                  tip.kind === "percent" && tip.percent === p
                    ? "bg-amber-400 border-amber-400 text-zinc-950"
                    : "bg-zinc-950 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                }`}
                data-testid={`delivery-tip-${p}`}
              >
                {p}%
              </button>
            ))}
            <button
              type="button"
              onClick={() => setTip((t) => ({ ...t, kind: "custom" }))}
              className={`px-3 py-2 rounded font-bold text-sm border transition ${
                tip.kind === "custom"
                  ? "bg-amber-400 border-amber-400 text-zinc-950"
                  : "bg-zinc-950 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
              }`}
              data-testid="delivery-tip-custom-btn"
            >
              Custom $
            </button>
            {tip.kind === "custom" && (
              <div className="relative flex-1 min-w-[140px]">
                <DollarSign className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 pointer-events-none"/>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={tip.custom}
                  onChange={(e) => setTip((t) => ({ ...t, custom: e.target.value }))}
                  placeholder="0.00"
                  data-testid="delivery-tip-custom-input"
                  className="bg-zinc-950 border-zinc-700 h-10 pl-6"
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => setTip({ kind: "none", percent: null, custom: "" })}
              className={`text-xs underline underline-offset-2 transition ${
                tip.kind === "none" ? "text-zinc-200" : "text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid="delivery-tip-none"
            >
              No tip
            </button>
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">
            Your total (base fee + per-stop + mileage + time + tax + tip) will be calculated by our dispatcher
            before payment authorization.
          </p>
        </Card>

        {/* v2.7.63+E46 · Live itemized transparency preview. Debounced
            300ms after any stop/tip change; server heuristic estimates
            miles/minutes from stop count. Dispatch finalizes with real
            miles once the manifest is picked up. */}
        {preview?.quote && (
          <Card
            className="bg-gradient-to-br from-emerald-500/5 to-zinc-900 border-emerald-400/40 p-4 sm:p-5 mb-4"
            data-testid="delivery-preview-card"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-emerald-300"/>
                <div>
                  <div className="text-sm font-bold text-zinc-100" data-testid="delivery-preview-title">
                    Estimated Quote
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300/80">
                    Pricing Transparency · Live Preview
                  </div>
                </div>
              </div>
              {previewLoading && (
                <span className="text-[10px] text-zinc-500" data-testid="delivery-preview-loading">
                  Updating…
                </span>
              )}
            </div>

            <div className="space-y-1.5 text-sm" data-testid="delivery-preview-lines">
              <PreviewRow
                label="Base Delivery Fee"
                value={preview.quote.base_fee}
                testid="preview-base"
              />
              <PreviewRow
                label={`Per-Stop Fee · ${preview.quote.stops_used} stop${preview.quote.stops_used === 1 ? "" : "s"}`}
                value={preview.quote.per_stop_fee_line}
                testid="preview-per-stop"
              />
              <PreviewRow
                label={`Distance · ~${preview.quote.estimated_miles.toFixed(1)} mi${preview.estimated ? " (est.)" : ""}`}
                value={preview.quote.miles_line}
                testid="preview-miles"
              />
              <PreviewRow
                label={`Time · ~${preview.quote.estimated_minutes.toFixed(0)} min${preview.estimated ? " (est.)" : ""}`}
                value={preview.quote.time_line}
                testid="preview-time"
              />
              {preview.quote.wait_line > 0 && (
                <PreviewRow
                  label={`Stop Wait Time · ${preview.quote.stops_used} × fee`}
                  value={preview.quote.wait_line}
                  testid="preview-wait"
                />
              )}
              <div className="border-t border-zinc-800 my-2"/>
              <PreviewRow
                label="Subtotal (fees)"
                value={preview.quote.fee_subtotal}
                muted
                testid="preview-fee-subtotal"
              />
              {preview.quote.tax_rate > 0 && (
                <PreviewRow
                  label={`Estimated Tax · ${preview.quote.tax_rate}%`}
                  value={preview.quote.tax_amount}
                  testid="preview-tax"
                />
              )}
              {preview.quote.tip_amount > 0 && (
                <PreviewRow
                  label={`Driver Tip${preview.quote.tip_source?.startsWith("percent") ? ` · ${preview.quote.tip_source.split("_")[1]}%` : ""}`}
                  value={preview.quote.tip_amount}
                  testid="preview-tip"
                  accent="amber"
                />
              )}
              <div className="border-t border-emerald-400/30 my-2"/>
              <div className="flex items-center justify-between text-base">
                <span className="font-bold text-zinc-100">Estimated Total</span>
                <span className="font-extrabold text-emerald-300 meter-display" data-testid="preview-total">
                  ${preview.quote.total.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="mt-3 flex items-start gap-2 text-[11px] text-zinc-400 bg-zinc-950/60 border border-zinc-800 rounded p-2">
              <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-sky-300"/>
              <div>
                Distance &amp; time above are heuristic estimates from your stop count.
                Our dispatcher confirms the exact miles/minutes on your route and finalizes
                your total before requesting payment authorization. Tax varies by jurisdiction.
              </div>
            </div>
          </Card>
        )}

        <SmsConsent
          value={smsConsent}
          onChange={setSmsConsent}
          testIdPrefix="delivery-sms-consent"
          className="pt-3 border-t border-zinc-800"
        />

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || busy}
          className="w-full h-12 bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold text-base"
          data-testid="delivery-submit"
        >
          {busy ? "Submitting…" : (
            <span className="inline-flex items-center gap-2">
              REQUEST DELIVERY QUOTE
              <ArrowRight className="h-4 w-4"/>
            </span>
          )}
        </Button>

        <p className="text-[10px] text-zinc-500 text-center mt-3 max-w-md mx-auto">
          By submitting, you agree Buller Taxi&apos;s dispatcher will contact you to confirm price,
          driver assignment, and pickup time. No payment is processed until you approve the quote.
        </p>
      </div>
    </MarketingLayout>
  );
}

// ── Sub-components ──
function Field({ label, icon, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 flex items-center gap-1 mb-1">
        <span className="text-zinc-500">{icon}</span>{label}
      </Label>
      {children}
    </div>
  );
}

function PreviewRow({ label, value, muted = false, accent, testid }) {
  const valueColor = accent === "amber" ? "text-amber-300"
    : muted ? "text-zinc-400"
    : "text-zinc-200";
  const labelColor = muted ? "text-zinc-500" : "text-zinc-400";
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={`text-[13px] ${labelColor}`}>{label}</span>
      <span className={`meter-display text-sm ${valueColor} ${muted ? "" : "font-semibold"}`}>
        ${(Number(value) || 0).toFixed(2)}
      </span>
    </div>
  );
}
